/**
 * 🔐 HEARTBEATS.FR - Auth Page SIMPLIFIÉE
 * 
 * NOUVEAU FLUX (sans Coffre/PIN) :
 * 1. Inscription (email + mdp + date de naissance)
 * 2. Vérification email (double opt-in obligatoire)
 * 3. Redirection vers le portail → Onboarding
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, Mail, Lock, User, ArrowRight, Eye, EyeOff, Calendar,
  Sparkles, Check, AlertCircle, Loader2, ArrowLeft, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Clés localStorage
const AGE_VERIFIED_KEY = 'heartbeats_age_verified';

const HeartbeatsAuth = ({ onSuccess, onBack, initialMode = 'signup' }) => {
  // États principaux
  const [step, setStep] = useState('auth'); // auth, verify_email
  const [mode, setMode] = useState(initialMode); // signup, login, forgot
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Données utilisateur temporaires (pour vérification email)
  const [pendingUser, setPendingUser] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  
  // 🔗 Code affilié (depuis cookie ou URL)
  const [affiliateRef, setAffiliateRef] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    birthDate: '',
    acceptTerms: false
  });

  // Date de naissance en 3 parties (plus facile à saisir)
  const [birthDay, setBirthDay] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthYear, setBirthYear] = useState('');

  // 🔗 Récupérer le code affilié au chargement
  useEffect(() => {
    // 1. Vérifier l'URL (param ?ref=XXX)
    const urlParams = new URLSearchParams(window.location.search);
    const refFromUrl = urlParams.get('ref');
    
    // 2. Vérifier les cookies
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    };
    const refFromCookie = getCookie('hb_ref');
    const clickIdFromCookie = getCookie('hb_click_id');
    
    // 3. Vérifier localStorage
    const refFromStorage = localStorage.getItem('hb_affiliate_ref');
    let storageData = null;
    if (refFromStorage) {
      try {
        storageData = JSON.parse(refFromStorage);
      } catch (e) {}
    }
    
    // Priorité: URL > Cookie > LocalStorage
    const affiliateCode = refFromUrl || refFromCookie || storageData?.code;
    const clickId = clickIdFromCookie || storageData?.clickId;
    
    if (affiliateCode) {
      console.log('🔗 Code affilié détecté:', affiliateCode);
      setAffiliateRef({ code: affiliateCode, clickId });
      
      // 🔗 TRACKING MODERNE - Appeler l'API pour tracker la visite
      const trackVisit = async () => {
        try {
          // Générer ou récupérer un visitor_id unique
          let visitorId = localStorage.getItem('hb_visitor_id');
          if (!visitorId) {
            visitorId = 'v_' + Math.random().toString(36).substr(2, 16);
            localStorage.setItem('hb_visitor_id', visitorId);
          }
          
          const response = await fetch(`${API_URL}/api/affiliation/track-visit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ref_code: affiliateCode,
              visitor_id: visitorId,
              source: urlParams.get('source') || urlParams.get('utm_source') || null,
              landing_page: window.location.pathname
            })
          });
          
          const data = await response.json();
          if (data.success) {
            console.log('✅ Visite trackée:', data.visitor_id);
            
            // Sauvegarder dans cookie (90 jours)
            const expires = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toUTCString();
            document.cookie = `hb_ref=${affiliateCode}; expires=${expires}; path=/; SameSite=Lax`;
            document.cookie = `hb_visitor_id=${data.visitor_id}; expires=${expires}; path=/; SameSite=Lax`;
            
            // Sauvegarder dans localStorage aussi
            localStorage.setItem('hb_affiliate_ref', JSON.stringify({
              code: affiliateCode,
              visitorId: data.visitor_id,
              trackedAt: new Date().toISOString()
            }));
          }
        } catch (error) {
          console.error('Erreur tracking affiliation:', error);
        }
      };
      
      trackVisit();
    }
  }, []);

  // Mettre à jour formData.birthDate quand les parties changent
  useEffect(() => {
    if (birthDay && birthMonth && birthYear) {
      const paddedDay = birthDay.padStart(2, '0');
      const paddedMonth = birthMonth.padStart(2, '0');
      setFormData(prev => ({
        ...prev,
        birthDate: `${birthYear}-${paddedMonth}-${paddedDay}`
      }));
    }
  }, [birthDay, birthMonth, birthYear]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  // Vérifier l'âge (18+)
  const checkAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age >= 18;
  };

  // Envoyer le code de vérification
  const sendVerificationCode = async (email, firstName) => {
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedCode(code);
    
    try {
      const response = await fetch(`${API_URL}/api/heartbeats/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          code, 
          customerId: `pending_${Date.now()}`,
          customerName: firstName 
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setCodeSent(true);
        return true;
      } else {
        setError('Erreur lors de l\'envoi du code. Réessaie.');
        return false;
      }
    } catch (err) {
      console.error('Erreur envoi code:', err);
      // Fallback - on continue quand même
      setCodeSent(true);
      return true;
    }
  };

  // Soumettre le formulaire principal
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'signup') {
        // Validations
        if (!formData.email || !formData.password || !formData.firstName || !formData.birthDate) {
          throw new Error('Tous les champs sont obligatoires');
        }
        if (!checkAge(formData.birthDate)) {
          throw new Error('Tu dois avoir 18 ans ou plus pour utiliser Heartbeats');
        }
        if (formData.password.length < 6) {
          throw new Error('Le mot de passe doit faire au moins 6 caractères');
        }
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Les mots de passe ne correspondent pas');
        }
        if (!formData.acceptTerms) {
          throw new Error('Tu dois accepter les conditions d\'utilisation');
        }

        // Sauvegarder l'âge vérifié
        localStorage.setItem(AGE_VERIFIED_KEY, JSON.stringify({ 
          birthDate: formData.birthDate, 
          verifiedAt: new Date().toISOString() 
        }));

        // Stocker les données en attente de vérification
        setPendingUser({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          birthDate: formData.birthDate
        });

        // Envoyer le code de vérification
        const sent = await sendVerificationCode(formData.email, formData.firstName);
        if (sent) {
          setStep('verify_email');
          setSuccess('');
        }

      } else if (mode === 'login') {
        // Connexion directe (pas de vérification email pour le login)
        if (!formData.email || !formData.password) {
          throw new Error('Email et mot de passe requis');
        }

        const res = await fetch(`${API_URL}/api/heartbeats/login-standalone`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password
          })
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Email ou mot de passe incorrect');
        }
        
        const data = await res.json();
        
        // 🔧 FIX CRITIQUE: Nettoyer tout l'ancien localStorage avant de sauvegarder
        // Cela évite les conflits avec d'anciens comptes/sessions corrompus
        localStorage.removeItem('heartbeats_token');
        localStorage.removeItem('heartbeats_user');
        localStorage.removeItem('lpep_customer');
        localStorage.removeItem('lpep_token');
        
        // Sauvegarder le nouveau token et les infos
        localStorage.setItem('heartbeats_token', data.token);
        localStorage.setItem('heartbeats_user', JSON.stringify(data.user));
        
        console.log('✅ Connexion réussie, ID:', data.user?.id);
        
        setSuccess('Connexion réussie !');
        setTimeout(() => {
          if (onSuccess) onSuccess(data);
        }, 1000);

      } else if (mode === 'forgot') {
        if (!formData.email) {
          throw new Error('Email requis');
        }

        const res = await fetch(`${API_URL}/api/heartbeats/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email })
        });

        setSuccess('Si cet email existe, tu recevras un lien de réinitialisation.');
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Vérifier le code email et créer le compte
  const handleVerifyCode = async () => {
    setError('');
    setLoading(true);

    try {
      // Vérifier le code
      if (verificationCode !== generatedCode) {
        throw new Error('Code incorrect. Vérifie et réessaie.');
      }

      // Le code est correct ! Créer le compte
      const res = await fetch(`${API_URL}/api/heartbeats/register-standalone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: pendingUser.email,
          password: pendingUser.password,
          firstName: pendingUser.firstName,
          birthDate: pendingUser.birthDate,
          source: 'heartbeats.fr',
          emailVerified: true
        })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors de la création du compte');
      }
      
      const data = await res.json();
      
      // 🔧 FIX CRITIQUE: Nettoyer tout l'ancien localStorage avant de sauvegarder
      // Cela évite les conflits avec d'anciens comptes/sessions corrompus
      localStorage.removeItem('heartbeats_token');
      localStorage.removeItem('heartbeats_user');
      localStorage.removeItem('lpep_customer');
      localStorage.removeItem('lpep_token');
      
      // Sauvegarder le nouveau token et les infos
      localStorage.setItem('heartbeats_token', data.token);
      localStorage.setItem('heartbeats_user', JSON.stringify(data.user));
      
      console.log('✅ Compte créé avec succès, ID:', data.user?.id);
      
      // 🔗 Lier le parrainage via le tracking moderne
      if (affiliateRef && data.user?.id) {
        try {
          const visitorId = localStorage.getItem('hb_visitor_id');
          
          await fetch(`${API_URL}/api/affiliation/link-referral`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              customer_id: data.user.id,
              visitor_id: visitorId || '',
              phone: pendingUser.phone || '',
              email: pendingUser.email
            })
          });
          console.log('🔗 Parrainage enregistré via tracking');
          
          // Nettoyer les données d'affiliation
          document.cookie = 'hb_ref=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
          document.cookie = 'hb_visitor_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
          localStorage.removeItem('hb_affiliate_ref');
          localStorage.removeItem('hb_visitor_id');
        } catch (e) {
          console.error('Erreur liaison parrainage:', e);
        }
      }
      
      setSuccess('Compte créé ! Bienvenue sur Heartbeats 💜');
      setTimeout(() => {
        if (onSuccess) onSuccess(data);
      }, 1500);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Renvoyer le code
  const handleResendCode = async () => {
    setError('');
    setCodeSent(false);
    setVerificationCode('');
    await sendVerificationCode(pendingUser.email, pendingUser.firstName);
  };

  // ========================================
  // RENDU - ÉTAPE VÉRIFICATION EMAIL
  // ========================================
  if (step === 'verify_email') {
    return (
      <div className="min-h-screen overflow-x-hidden overflow-y-auto bg-gradient-to-b from-[#0a0015] via-[#1a0033] to-[#0a0015] text-white flex items-center justify-center p-4" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
        {/* Particules */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-pink-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 w-full max-w-md"
        >
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
            
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4">
                <Mail className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-black">📧 Vérifie ton email</h1>
              <p className="text-white/50 text-sm mt-2">
                Un code à 6 chiffres a été envoyé à
              </p>
              <p className="text-cyan-400 font-medium">{pendingUser?.email}</p>
            </div>

            {/* Messages */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center gap-2 bg-red-500/20 border border-red-500/30 text-red-300 px-4 py-3 rounded-xl mb-4 text-sm"
                >
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  {error}
                </motion.div>
              )}
              {success && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center gap-2 bg-green-500/20 border border-green-500/30 text-green-300 px-4 py-3 rounded-xl mb-4 text-sm"
                >
                  <Check className="w-5 h-5 flex-shrink-0" />
                  {success}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Code Input */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-white/70 mb-2 text-center">
                  <Shield className="w-4 h-4 inline mr-1" />
                  Entre le code de vérification
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="w-full bg-white/10 border-2 border-cyan-500/50 rounded-xl py-4 px-4 text-white text-center text-3xl font-mono tracking-[0.5em] placeholder-white/20 focus:outline-none focus:border-cyan-400 transition-colors"
                  data-testid="email-verification-code"
                  autoFocus
                />
              </div>

              {/* Verify Button */}
              <button
                onClick={handleVerifyCode}
                disabled={loading || verificationCode.length !== 6}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white py-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="verify-code-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Vérifier et créer mon compte
                  </>
                )}
              </button>

              {/* Aide */}
              <div className="space-y-3 mt-6">
                <p className="text-yellow-400 text-sm text-center">
                  ⚠️ Vérifie tes spams si tu ne vois pas l'email !
                </p>

                {/* Bouton Renvoyer */}
                <button
                  onClick={handleResendCode}
                  className="w-full py-3 px-4 rounded-xl bg-white/5 border border-white/20 text-white/70 hover:bg-white/10 hover:text-white transition-all flex items-center justify-center gap-2"
                  data-testid="resend-code-btn"
                >
                  📧 Email non reçu ? Renvoyer le code
                </button>

                {/* Bouton Secours */}
                <button
                  onClick={() => {
                    if (window.confirm('Tu n\'as vraiment pas reçu l\'email ?\n\nVérifie bien tes SPAMS !\n\nSi tu cliques OK, le code sera affiché.')) {
                      alert(`🔐 Ton code de vérification : ${generatedCode}`);
                    }
                  }}
                  className="w-full py-2 px-4 rounded-xl bg-orange-500/10 border border-orange-500/30 text-orange-400 text-sm hover:bg-orange-500/20 transition-all"
                  data-testid="show-code-btn"
                >
                  🆘 Toujours pas reçu ? Voir le code
                </button>
              </div>

              {/* Retour */}
              <button
                onClick={() => {
                  setStep('auth');
                  setVerificationCode('');
                  setCodeSent(false);
                }}
                className="w-full text-white/50 hover:text-white py-2 transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Modifier mon email
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // ========================================
  // RENDU - FORMULAIRE PRINCIPAL
  // ========================================
  return (
    <div className="min-h-screen overflow-x-hidden overflow-y-auto bg-gradient-to-b from-[#0a0015] via-[#1a0033] to-[#0a0015] text-white flex items-center justify-center p-4" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
      
      {/* Particules d'arrière-plan */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-pink-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Étoiles */}
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              opacity: Math.random() * 0.5 + 0.2
            }}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Back Button */}
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour
          </button>
        )}

        {/* Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
          
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center mb-4">
              <Heart className="w-8 h-8 text-white" fill="white" />
            </div>
            <h1 className="text-2xl font-black">
              {mode === 'signup' && 'Rejoins Heartbeats 💜'}
              {mode === 'login' && 'Content de te revoir !'}
              {mode === 'forgot' && 'Mot de passe oublié'}
            </h1>
            <p className="text-white/50 text-sm mt-1">
              {mode === 'signup' && 'Crée ton compte en 30 secondes'}
              {mode === 'login' && 'Connecte-toi pour continuer'}
              {mode === 'forgot' && 'On t\'envoie un lien de réinitialisation'}
            </p>
          </div>

          {/* Error / Success Messages */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-center gap-2 bg-red-500/20 border border-red-500/30 text-red-300 px-4 py-3 rounded-xl mb-4 text-sm"
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                {error}
              </motion.div>
            )}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-center gap-2 bg-green-500/20 border border-green-500/30 text-green-300 px-4 py-3 rounded-xl mb-4 text-sm"
              >
                <Check className="w-5 h-5 flex-shrink-0" />
                {success}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Prénom - Signup only */}
            {mode === 'signup' && (
              <div>
                <label className="block text-sm text-white/70 mb-1">Prénom</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Ton prénom"
                    className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder-white/30 focus:outline-none focus:border-pink-500/50 transition-colors"
                    required
                    data-testid="signup-firstname"
                  />
                </div>
              </div>
            )}

            {/* Date de naissance - Signup only */}
            {mode === 'signup' && (
              <div>
                <label className="block text-sm text-white/70 mb-1">
                  Date de naissance <span className="text-pink-400">(18+ requis)</span>
                </label>
                
                {/* 📅 NOUVEAU: 3 sélecteurs Jour/Mois/Année - Beaucoup plus facile ! */}
                <div className="flex gap-2">
                  {/* Jour */}
                  <div className="relative flex-1">
                    <select
                      value={birthDay}
                      onChange={(e) => setBirthDay(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-3 text-white focus:outline-none focus:border-pink-500/50 transition-colors appearance-none cursor-pointer"
                      required
                      data-testid="signup-birthdate-day"
                    >
                      <option value="" className="bg-[#1a0a2e] text-white/50">Jour</option>
                      {[...Array(31)].map((_, i) => (
                        <option key={i + 1} value={String(i + 1)} className="bg-[#1a0a2e]">
                          {String(i + 1).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-white/30">▼</div>
                  </div>
                  
                  {/* Mois */}
                  <div className="relative flex-1">
                    <select
                      value={birthMonth}
                      onChange={(e) => setBirthMonth(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-3 text-white focus:outline-none focus:border-pink-500/50 transition-colors appearance-none cursor-pointer"
                      required
                      data-testid="signup-birthdate-month"
                    >
                      <option value="" className="bg-[#1a0a2e] text-white/50">Mois</option>
                      {['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'].map((month, i) => (
                        <option key={i + 1} value={String(i + 1)} className="bg-[#1a0a2e]">
                          {month}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-white/30">▼</div>
                  </div>
                  
                  {/* Année */}
                  <div className="relative flex-1">
                    <select
                      value={birthYear}
                      onChange={(e) => setBirthYear(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-3 text-white focus:outline-none focus:border-pink-500/50 transition-colors appearance-none cursor-pointer"
                      required
                      data-testid="signup-birthdate-year"
                    >
                      <option value="" className="bg-[#1a0a2e] text-white/50">Année</option>
                      {/* Années de 18 ans minimum jusqu'à 80 ans max */}
                      {[...Array(63)].map((_, i) => {
                        const year = new Date().getFullYear() - 18 - i;
                        return (
                          <option key={year} value={String(year)} className="bg-[#1a0a2e]">
                            {year}
                          </option>
                        );
                      })}
                    </select>
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-white/30">▼</div>
                  </div>
                </div>
                
                {/* Affichage de l'âge calculé */}
                {birthDay && birthMonth && birthYear && (
                  <p className="text-xs text-white/50 mt-1">
                    {checkAge(formData.birthDate) ? (
                      <span className="text-green-400">✓ Vous avez {new Date().getFullYear() - parseInt(birthYear)} ans</span>
                    ) : (
                      <span className="text-red-400">✗ Vous devez avoir 18 ans minimum</span>
                    )}
                  </p>
                )}
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm text-white/70 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="ton@email.com"
                  className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder-white/30 focus:outline-none focus:border-pink-500/50 transition-colors"
                  required
                  data-testid="auth-email"
                />
              </div>
            </div>

            {/* Password */}
            {mode !== 'forgot' && (
              <div>
                <label className="block text-sm text-white/70 mb-1">Mot de passe</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-12 text-white placeholder-white/30 focus:outline-none focus:border-pink-500/50 transition-colors"
                    required
                    minLength={6}
                    data-testid="auth-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-white/50 hover:text-white transition-colors p-1"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}

            {/* Confirm Password - Signup only */}
            {mode === 'signup' && (
              <div>
                <label className="block text-sm text-white/70 mb-1">Confirme ton mot de passe</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder-white/30 focus:outline-none focus:border-pink-500/50 transition-colors"
                    required
                    data-testid="signup-confirm-password"
                  />
                </div>
              </div>
            )}

            {/* Terms - Signup only */}
            {mode === 'signup' && (
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-white/20 bg-white/5 text-pink-500 focus:ring-pink-500 focus:ring-offset-0 mt-0.5"
                  data-testid="signup-terms"
                />
                <span className="text-sm text-white/60">
                  J&apos;accepte les{' '}
                  <a href="/heartbeats-cgu" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:underline">CGU</a>
                  {' '}et la{' '}
                  <a href="/heartbeats-confidentialite" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:underline">politique de confidentialité</a>
                  {' '}et je confirme avoir <span className="text-pink-400 font-bold">18 ans ou plus</span>.
                </span>
              </label>
            )}

            {/* Forgot Password Link - Login only */}
            {mode === 'login' && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setMode('forgot')}
                  className="text-sm text-pink-400 hover:text-pink-300 transition-colors"
                >
                  Mot de passe oublié ?
                </button>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white py-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-lg"
              data-testid="auth-submit"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {mode === 'signup' && (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Créer mon compte
                    </>
                  )}
                  {mode === 'login' && (
                    <>
                      Se connecter
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                  {mode === 'forgot' && 'Envoyer le lien'}
                </>
              )}
            </button>
          </form>

          {/* Toggle Mode */}
          <div className="text-center mt-6 text-sm">
            {mode === 'signup' && (
              <p className="text-white/50">
                Déjà un compte ?{' '}
                <button
                  onClick={() => setMode('login')}
                  className="text-pink-400 hover:text-pink-300 font-medium transition-colors"
                >
                  Se connecter
                </button>
              </p>
            )}
            {mode === 'login' && (
              <p className="text-white/50">
                Pas encore de compte ?{' '}
                <button
                  onClick={() => setMode('signup')}
                  className="text-pink-400 hover:text-pink-300 font-medium transition-colors"
                >
                  S'inscrire
                </button>
              </p>
            )}
            {mode === 'forgot' && (
              <p className="text-white/50">
                <button
                  onClick={() => setMode('login')}
                  className="text-pink-400 hover:text-pink-300 font-medium transition-colors"
                >
                  ← Retour à la connexion
                </button>
              </p>
            )}
          </div>

          {/* Info sécurité */}
          {mode === 'signup' && (
            <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
              <p className="text-xs text-white/40 text-center">
                🔒 Tes données sont protégées et ne seront jamais vendues.
                <br />
                Heartbeats respecte le RGPD.
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default HeartbeatsAuth;
